import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { AuthLayout } from "./authLayout";
import { AuthHeader } from "./authHeader";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import { getPlatformFromUrl } from "../../utils/platform-mapping";
import { register } from "../../utils/auth";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    "& fieldset": {
      borderColor: "#5f6368",
    },
    "&:hover fieldset": {
      borderColor: "#5f6368",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#bdc1c6",
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  "& input": {
    fontSize: "1.1rem",
    padding: "16px 14px",
  },
}));

interface CreateAccountProps {
  onSubmit: () => void;
  onBack: () => void;
}

const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionForm = motion.form;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
    },
  },
};

const formItemVariants = {
  hidden: {
    opacity: 0,
    x: 50, // Start from right
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1, // Stagger delay based on index
      ease: [0.4, 0, 0.2, 1], // Custom easing for smooth motion
    },
  }),
};

export const CreateAccount = ({ onSubmit, onBack }: CreateAccountProps) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const platform = getPlatformFromUrl();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Please enter your name";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Please enter a password";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setError(newErrors);
      return;
    }

    try {
      setIsLoading(true);

      await register({
        email: formData.email,
        password: formData.password,
        fullName: formData.name,
      });
    } catch (err) {
      setError({ submit: "An error occurred. Please try again." });
    } finally {
      onBack();
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error[name]) {
      setError((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <AuthLayout>
      <MotionBox
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 8, md: 16 },
          alignItems: { xs: "stretch", md: "center" },
          width: "100%",
          height: "100%",
          justifyContent: "center",
          minHeight: { xs: "300px", md: "340px" },
        }}
      >
        {/* Left Column - Header - No animations */}
        <Box
          sx={{
            flex: { md: "0 1 40%" },
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <AuthHeader
            title="Sign Up"
            subtitle={`to continue to ${platform.name}`}
          />
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: "#2563EB", // Blue-600
              fontSize: "0.9rem",
              mt: 3,
              pl: 0,
              "&:hover": {
                backgroundColor: "transparent",
                color: "#1D4ED8", // Blue-700
              },
            }}
          >
            Back to sign in
          </Button>
        </Box>

        {/* Right Column - Form */}
        <MotionForm
          onSubmit={handleSubmit}
          variants={containerVariants}
          style={{
            flex: "0 1 60%",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            paddingBottom: "80px",
          }}
        >
          <AnimatePresence mode="wait">
            <MotionBox key="name-field" custom={0} variants={formItemVariants}>
              <StyledTextField
                fullWidth
                label="Full name"
                variant="outlined"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!error.name}
                helperText={error.name}
                required
                disabled={isLoading}
                autoComplete="name"
              />
            </MotionBox>

            <MotionBox key="email-field" custom={1} variants={formItemVariants}>
              <StyledTextField
                fullWidth
                label="Email address"
                variant="outlined"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!error.email}
                helperText={error.email}
                required
                disabled={isLoading}
                autoComplete="email"
                inputProps={{
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: "false",
                  dir: "ltr",
                }}
              />
            </MotionBox>

            <MotionBox
              key="password-field"
              custom={2}
              variants={formItemVariants}
            >
              <StyledTextField
                fullWidth
                label="Password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={!!error.password}
                helperText={error.password}
                required
                disabled={isLoading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MotionBox>

            <MotionBox
              key="confirm-password-field"
              custom={3}
              variants={formItemVariants}
            >
              <StyledTextField
                fullWidth
                label="Confirm password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!error.confirmPassword}
                helperText={error.confirmPassword}
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </MotionBox>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {error.submit && (
              <MotionBox
                key="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                sx={{ color: "error.main", textAlign: "center", mt: 1 }}
              >
                {error.submit}
              </MotionBox>
            )}
          </AnimatePresence>

          <MotionButton
            key="submit-button"
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading}
            loading={isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            sx={{
              bgcolor: "#2563EB", // Blue-600
              color: "#FFFFFF",
              fontSize: "0.95rem",
              py: 1.5,
              px: 5,
              mt: 2,
              mb: { xs: 4, sm: 2 },
              minWidth: { xs: "100%", sm: "auto" },
              "&:hover": {
                bgcolor: "#1D4ED8", // Blue-700
              },
              "&:disabled": {
                bgcolor: "rgba(37, 99, 235, 0.5)", // Blue-600 with opacity
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
            ) : (
              "Create account"
            )}
          </MotionButton>
        </MotionForm>
      </MotionBox>
    </AuthLayout>
  );
};
